import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { KhisSubmissionListView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  TextArea,
} from '@wodi/ui';
import { useState, type ReactNode } from 'react';

import { khisKeys, khisSubmissionsQuery, prepareKhisSubmission } from '../api/khis.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { BackToReports } from '../reports/report-frame.tsx';
import { toasts } from '../toasts.ts';
import {
  errorText,
  LoadFailed,
  periodWords,
  recentPeriods,
  SubmissionTag,
  whenWords,
} from './format.tsx';
import styles from './khis.module.css';

// Reports > KHIS submissions (docs/plans/phase3-khis-and-quality.md §4): the facility's
// months, their state in words, and preparing a month or a correction. Only aggregate
// counts are ever sent; someone else approves before anything leaves Wodi.

const MONTHS_OFFERED = 12;

function Frame({ children }: { children: ReactNode }) {
  return (
    <div className={styles.page}>
      <BackToReports />
      <PageHeader
        title="KHIS submissions"
        description="Monthly counts for KHIS. A month is prepared, checked and approved by a second person, then sent."
      />
      {children}
    </div>
  );
}

function PrepareMonth({ list }: { list: KhisSubmissionListView }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const online = useOnline();
  const periods = recentPeriods(list.lastCompletePeriod, MONTHS_OFFERED);
  const [period, setPeriod] = useState(list.lastCompletePeriod);
  const [reason, setReason] = useState('');
  const approvedAlready = list.items.some(
    (i) =>
      i.period === period && (i.state === 'waiting' || i.state === 'sent' || i.state === 'failed'),
  );
  const prepare = useMutation({
    mutationFn: () =>
      prepareKhisSubmission({
        period,
        correctionReason: approvedAlready ? reason.trim() : null,
      }),
    onSuccess: async (view) => {
      await queryClient.invalidateQueries({ queryKey: khisKeys.submissions });
      toasts.show({ title: `${periodWords(view.period)} prepared` });
      await navigate({ to: '/reports/khis/$submissionId', params: { submissionId: view.id } });
    },
  });
  const reasonMissing = approvedAlready && reason.trim().length < 3;
  return (
    <Section
      title="Prepare a month"
      description="Wodi counts the month from its records and keeps the figures as they are now, for checking."
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (!reasonMissing) prepare.mutate();
        }}
      >
        <Select
          label="Month"
          width="sm"
          options={periods.map((p) => ({ id: p, label: periodWords(p) }))}
          value={period}
          onChange={(key) => {
            if (key !== null) setPeriod(String(key));
          }}
        />
        {approvedAlready ? (
          <TextArea
            label="Reason for the correction"
            description="This month was already approved. The corrected figures replace it in KHIS once approved."
            value={reason}
            onChange={setReason}
          />
        ) : null}
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online || prepare.isPending || reasonMissing}
        >
          {approvedAlready ? 'Prepare a correction' : 'Prepare month'}
        </Button>
      </form>
      {prepare.error === null ? null : (
        <Notice tone="error">
          {errorText(prepare.error, 'The month was not prepared. Try again.')}
        </Notice>
      )}
    </Section>
  );
}

function SetupMissing({ list, canManage }: { list: KhisSubmissionListView; canManage: boolean }) {
  const missing = [
    ...(list.hasActiveMapping ? [] : ['the KHIS mapping']),
    ...(list.facility.orgUnitUid === null
      ? [`${list.facility.name}'s KHIS organisation unit`]
      : []),
  ];
  if (missing.length === 0) return null;
  return (
    <Notice tone="info">
      {`Months can't be prepared until ${missing.join(' and ')} ${missing.length === 1 ? 'is' : 'are'} set. `}
      {canManage ? (
        <Link to="/settings/khis">Set it up in Settings, KHIS.</Link>
      ) : (
        'Ask an administrator to set it up in Settings, KHIS.'
      )}
    </Notice>
  );
}

export function KhisSubmissionsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'khis.read');
  const list = useQuery({ ...khisSubmissionsQuery, enabled: allowed });
  const online = useOnline();
  if (session === null || !allowed)
    return (
      <Frame>
        <Notice tone="info">
          You don&apos;t have permission to see KHIS submissions. Ask an administrator if you need
          it.
        </Notice>
      </Frame>
    );
  const canPrepare = can(session, 'khis.submission.prepare');
  return (
    <Frame>
      {online ? null : (
        <Notice tone="info">
          You&apos;re offline. The list shows what was last loaded; preparing and approving need the
          connection.
        </Notice>
      )}
      {list.isPending ? <LoadingBlock label="Loading KHIS submissions" rows={4} /> : null}
      {list.isError ? (
        <LoadFailed error={list.error} what="KHIS submissions" retry={() => void list.refetch()} />
      ) : null}
      {list.isSuccess ? (
        <>
          <SetupMissing list={list.data} canManage={can(session, 'khis.mapping.manage')} />
          {canPrepare && list.data.hasActiveMapping && list.data.facility.orgUnitUid !== null ? (
            <PrepareMonth list={list.data} />
          ) : null}
          <Section title={`Months for ${list.data.facility.name}`}>
            {list.data.items.length === 0 ? (
              <EmptyState>
                No months prepared yet. A prepared month appears here for a second person to
                approve.
              </EmptyState>
            ) : (
              <TableScroll>
                <Table flat aria-label="KHIS submissions">
                  <TableHeader>
                    <Column isRowHeader>Month</Column>
                    <Column>State</Column>
                    <Column>Prepared</Column>
                    <Column>Approved</Column>
                  </TableHeader>
                  <TableBody items={list.data.items}>
                    {(item) => (
                      <Row id={item.id}>
                        <Cell>
                          <Link to="/reports/khis/$submissionId" params={{ submissionId: item.id }}>
                            {item.isCorrection
                              ? `${periodWords(item.period)}, correction`
                              : periodWords(item.period)}
                          </Link>
                        </Cell>
                        <Cell>
                          <SubmissionTag state={item.state} />
                        </Cell>
                        <Cell>{`${whenWords(item.preparedAt)} by ${item.preparedBy.displayName}`}</Cell>
                        <Cell>
                          {item.approvedAt === null || item.approvedBy === null
                            ? 'Not yet'
                            : `${whenWords(item.approvedAt)} by ${item.approvedBy.displayName}`}
                        </Cell>
                      </Row>
                    )}
                  </TableBody>
                </Table>
              </TableScroll>
            )}
          </Section>
        </>
      ) : null}
    </Frame>
  );
}
